import { useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { isSafeRedirect } from '../lib/redirectGuard';
import { TENANT_ACCESS_REVOKED, TENANT_ACCESS_REVOKED_LOGIN_ERROR } from '../lib/tenantAccessRevoked';

/**
 * #3248 PR 2 — the magic-link page (founder ruling, #3248 comment 6093741652).
 *
 * The emailed link is `${APP_URL}/action#t=<token>`. The token is in the URL
 * FRAGMENT, which the browser never sends to any server, so it stays out of
 * nginx/proxy/API logs and Referer headers, and an email scanner that
 * pre-fetches the link gets this SPA shell and consumes nothing.
 *
 * On mount the fragment is read once and immediately stripped from the address
 * bar (history.replaceState), so it doesn't linger in history or get copied.
 * Nothing is redeemed until the recipient clicks Continue: then
 * POST /action/redeem { token } (credentials: 'include'), which consumes the
 * link and sets the HttpOnly action-session cookie on the API host.
 *
 *   200 → redirectTo (through isSafeRedirect; dropped → /projects)
 *   410 → /link-expired
 *   401 TENANT_ACCESS_REVOKED → /login?error=tenant_access_revoked
 *   anything else (429, network) → stay, say so, allow another click
 *
 * A stored full login doesn't change this flow: the redeem is sent without the
 * Bearer header, so a refusal never clears the stored login.
 */

interface RedeemResponse {
  actionType: string;
  target: { kind: string; id: string } | null;
  redirectTo: unknown;
}

/** `t` from a `#t=…` fragment, or null. */
export function tokenFromHash(hash: string | null | undefined): string | null {
  if (!hash || hash.length < 2) return null;
  const t = new URLSearchParams(hash.slice(1)).get('t');
  return t ? t : null;
}

export default function ActionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // Read once, at first render; never re-read after the fragment is stripped.
  const [token] = useState(() => tokenFromHash(location.hash || window.location.hash));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  // Before anything else (layout effect: before paint, before any network call).
  useLayoutEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
  }, []);

  if (!token) return <Navigate to="/link-expired" replace />;

  const onContinue = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      const res = await apiFetch<RedeemResponse>('/action/redeem', {
        method: 'POST',
        body: JSON.stringify({ token }),
        omitBearer: true,
      });
      const target = typeof res?.redirectTo === 'string' && isSafeRedirect(res.redirectTo) ? res.redirectTo : '/projects';
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        navigate('/link-expired', { replace: true });
        return;
      }
      if (err instanceof ApiError && err.status === 401 && err.body?.code === TENANT_ACCESS_REVOKED) {
        navigate(`/login?error=${TENANT_ACCESS_REVOKED_LOGIN_ERROR}`, { replace: true });
        return;
      }
      setError(err instanceof ApiError && err.status === 429
        ? 'Too many attempts. Wait a minute and try again.'
        : 'Something went wrong. Please try again.');
      inFlight.current = false;
      setPending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)', padding: 16 }}>
      <div className="text-center max-w-md">
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          You’ve been asked to review an approval
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
          Continue to open it. This link works once and expires after 7 days.
        </p>
        <button
          type="button"
          onClick={onContinue}
          disabled={pending}
          aria-busy={pending}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            background: '#8b5cf6',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 24px',
            cursor: pending ? 'wait' : 'pointer',
            opacity: pending ? 0.7 : 1,
          }}
        >
          Continue
        </button>
        {error && (
          <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--error)', marginTop: 16 }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
